'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useGetConversationsQuery } from '@/api/features/conversations';
import { reduxLogger } from '@/core/utils/logger';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { EoWelcomeModal } from './EoWelcomeModal';
import { EoCompleteModal } from './EoCompleteModal';
import { EO_GUARDRAILS_TOUR_STEPS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = EO_GUARDRAILS_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * EoGuardrailsTourOverlay - Renders the E&O Guardrails tour step UI.
 *
 * Only renders when the active tour is 'eo-guardrails'. Navigates the user
 * to the Voley Support conversation so they can try typing risk phrases
 * and experience the guardrail system firsthand.
 */
export function EoGuardrailsTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isEoTour = activeTourId === 'eo-guardrails';

  // Auto-advance polling
  useTourAutoAdvance(isEoTour, currentStep, nextStep);

  // Find the Voley Support conversation for navigation
  const { data: searchResults } = useGetConversationsQuery(
    { search: 'Voley Support', limit: 1 },
    { skip: !isEoTour, refetchOnMountOrArgChange: 5 * 60 },
  );
  const voleySupportId = searchResults?.items?.[0]?.id;

  // Track which steps we've already navigated for (to avoid re-navigating)
  const navigatedForStepRef = useRef<string | null>(null);

  // Handle step-specific navigation explicitly in the overlay
  useEffect(() => {
    if (!isEoTour || !currentStep) return;
    if (navigatedForStepRef.current === currentStep.id) return;

    // Steps that need Voley Support conversation open
    const stepsNeedingConversation = [
      'highlight_type_high_risk',
      'highlight_warning_banner',
      'highlight_expand_details',
      'highlight_details_content',
      'eo_context_matters',
      'highlight_open_sidebar',
      'highlight_notes_section',
    ];
    if (stepsNeedingConversation.includes(currentStep.id) && voleySupportId) {
      navigatedForStepRef.current = currentStep.id;
      router.push(`/dashboard?view=all&conversation=${voleySupportId}`);
      reduxLogger.info(`[EoGuardrailsTour] Navigating to Voley Support for step: ${currentStep.id}`);
    }
  }, [isEoTour, currentStep?.id, currentStep, voleySupportId, router]);

  const handleWelcomeComplete = useCallback(() => {
    if (voleySupportId) {
      router.push(`/dashboard?view=all&conversation=${voleySupportId}`);
      reduxLogger.info('[EoGuardrailsTour] Navigating to Voley Support conversation');
    } else {
      router.push('/dashboard?view=all');
      reduxLogger.warn('[EoGuardrailsTour] Voley Support conversation not found, navigating to dashboard');
    }
    nextStep();
  }, [voleySupportId, router, nextStep]);

  const handleFinish = useCallback(() => {
    completeTourWithTracking('eo-guardrails');
    router.push('/dashboard?view=all');
  }, [completeTourWithTracking, router]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isEoTour || !isActive || !currentStep) return null;

  // Backdrop visibility — block clicks only for non-interactive, non-auto-advancing highlight steps
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'eo_welcome' && <EoWelcomeModal onComplete={handleWelcomeComplete} />}
          {currentStep.id === 'eo_complete' && <EoCompleteModal onFinish={handleFinish} />}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
